import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Button } from "@shopify/polaris";
import Modal from "components/Modal";
import { showAuthErrorModal, hideAuthErrorModal } from "../../redux/features/auth/authErrorSlice";
import useAppDispatch from "hooks/useAppDispatch";

const normalizeAuthError = (message) => {
	if (!message || typeof message !== 'string') {
		return message;
	}

	const normalized = message.trim();
	
	if (normalized === "Invitation not found" || normalized.includes("Invitation not found")) {
		return "Please sign in with the Google account for the email this invitation was sent to.";
	}

	return message;
};

const getDismissedKey = (authErrorValue) => {
	let hash = 0;
	for (let i = 0; i < authErrorValue.length; i++) {
		const char = authErrorValue.charCodeAt(i);
		hash = ((hash << 5) - hash) + char;
		hash = hash & hash;
	}
	return `auth_error_dismissed:${Math.abs(hash)}`;
};

const isDismissed = (authErrorValue) => {
	if (!authErrorValue) return false;
	const key = getDismissedKey(authErrorValue);
	return sessionStorage.getItem(key) === 'true';
};

const setDismissed = (authErrorValue) => {
	if (!authErrorValue) return;
	const key = getDismissedKey(authErrorValue);
	sessionStorage.setItem(key, 'true');
};

const AuthErrorModal = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();
	const showModal = useSelector((state) => state.authError?.showModal || false);
	const message = useSelector((state) => state.authError?.message || "");

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const raw = params.get("auth_error");

		if (raw) {
			if (isDismissed(raw)) {
				if (showModal) {
					dispatch(hideAuthErrorModal());
				}
				return;
			}

			let decodedMessage;
			try {
				decodedMessage = decodeURIComponent(raw);
			} catch (err) {
				console.warn("Failed to decode auth_error parameter:", err);
				decodedMessage = raw;
			}

			const finalMessage = normalizeAuthError(decodedMessage);
			dispatch(showAuthErrorModal(finalMessage));
		} else {
			if (showModal) {
				dispatch(hideAuthErrorModal());
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const handleClose = () => {
		const params = new URLSearchParams(location.search);
		const raw = params.get("auth_error");
		
		if (raw) {
			setDismissed(raw);
		}
		
		dispatch(hideAuthErrorModal());
	};

	return (
		<Modal
			visible={showModal}
			onClose={handleClose}
			title="Sign-in error"
			description={message}
			iconType="warning"
			closeIconVisible={true}
			shouldCloseOnOverlayClick={true}
			footer={
				<div style={{ display: "flex", justifyContent: "flex-end" }}>
					<Button variant="primary" onClick={handleClose}>
						OK
					</Button>
				</div>
			}
		/>
	);
};

export default AuthErrorModal;
